import { ReactNode } from 'react'
import {
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  styled,
} from '@mui/material'
import type { Definition } from '../../../types'
import InfoTooltip from '../../InfoTooltip/InfoTooltip'
import ChipRow from './ChipRow'
import classNames from 'classnames'

const CustomSelect = styled(Select)(() => ({
  '& .MuiSelect-select': {
    padding: '2px 8px',
    margin: 0,
    border: '1px solid #E9E7E1',
    background: '#222222 0% 0% no-repeat padding-box',
    textTransform: 'uppercase',
    fontSize: '14px',
    color: '#E9E7E1',
    fontWeight: '600',
  },
  '& .MuiSelect-icon': {
    color: '#E9E7E1',
  },
}))

const CustomMobileSelect = styled(Select)(() => ({
  '& .MuiSelect-select': {
    padding: '8px',
    margin: 0,
    border: '1px solid #E9E7E1',
    '&:focus': {
      border: '1px solid #E9E7E1',
    },
    background: '#222222 0% 0% no-repeat padding-box',
    textTransform: 'uppercase',
    fontSize: '18px',
    color: '#E9E7E1',
    fontWeight: '600',
  },
  '& .MuiSelect-icon': {
    color: '#E9E7E1',
  },
}))

interface MultipleSelectProps {
  options: Definition[]
  selectedOptions: string[]
  description: string
  placeholder: string
  onChange: (event: SelectChangeEvent<any>, child: ReactNode) => void
  onChipClick: (option: string) => void
  isMobile?: boolean
  hideInfoTooltip?: boolean
}

const MultipleSelect = ({
  options,
  selectedOptions,
  description,
  placeholder,
  onChange,
  onChipClick,
  isMobile = false,
  hideInfoTooltip = false,
}: MultipleSelectProps) => {
  const SelectComponent = isMobile ? CustomMobileSelect : CustomSelect
  return (
    <Stack>
      <Stack direction="row" className="items-center gap-2 mb-2">
        <SelectComponent
          className={classNames(isMobile ? 'w-full' : 'w-[240px]')}
          value={selectedOptions}
          displayEmpty
          onChange={onChange}
          multiple
          renderValue={() => {
            return placeholder
          }}
        >
          {options.map((option) => (
            <MenuItem
              className="text-white text-[12px]"
              key={`menu-item-for-${option.id}`}
              value={option.display_label}
              id={option.id}
              sx={{
                fontSize: '12px',
                color: '#E9E7E1',
                fontFamily: 'Montserrat',
                margin: 0,
                padding: '8px',
                borderBottom: `1px solid #E9E7E1`,
                borderLeft: `1px solid #E9E7E1`,
                borderRight: `1px solid #E9E7E1`,
                whiteSpace: 'normal',
                '&:first-of-type': {
                  borderTop: `1px solid #E9E7E1`,
                },
                ...(isMobile ? { width: 'full' } : { width: '240px' }),
              }}
            >
              {option.display_label}
            </MenuItem>
          ))}
        </SelectComponent>
        {!hideInfoTooltip && <InfoTooltip title={description} />}
      </Stack>
      {selectedOptions.length > 0 && (
        <ChipRow
          selectedOptions={selectedOptions}
          onClick={onChipClick}
          isMobile={isMobile}
        />
      )}
    </Stack>
  )
}
export default MultipleSelect
